import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, AlertCircle, PenLine, ListChecks } from 'lucide-react';
import { referenceService } from '@/services/referenceService';
import { ProcedureCatalogItem } from '@/types/reference.types';
import { groupByCategory } from '@/utils/groupIcd10Codes';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/use-toast';

interface ProcedureCatalogAutocompleteProps {
  procedureTypeId?: number | null;
  catalogId?: number | null;
  textValue?: string; // current procedure_name (used for free-text mode / display fallback)
  onSelect: (item: ProcedureCatalogItem) => void; // sets procedure_catalog_id + procedure_name
  onFreeText: (name: string) => void; // sets procedure_name, clears procedure_catalog_id
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function ProcedureCatalogAutocomplete({
  procedureTypeId,
  catalogId,
  textValue,
  onSelect,
  onFreeText,
  isDisabled = false,
  placeholder = 'Caută procedură (nume sau cod IPCCC)...',
  className,
}: ProcedureCatalogAutocompleteProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [catalog, setCatalog] = useState<ProcedureCatalogItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ProcedureCatalogItem | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Free-text is the escape hatch for procedures not present in the curated
  // catalog. Default to it when editing an existing procedure that has a
  // name but no catalog match (legacy/custom entries).
  const [isFreeText, setIsFreeText] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (catalogId && catalog.length > 0) {
      const item = catalog.find((c) => c.id === catalogId);
      if (item) {
        setSelectedItem(item);
        setIsFreeText(false);
        setSearchTerm('');
        return;
      }
    }
    if (!catalogId) {
      setSelectedItem(null);
      if (textValue) {
        setIsFreeText(true);
      }
    }
  }, [catalogId, catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCatalog = async () => {
    setIsLoading(true);
    try {
      const data = await referenceService.getProcedureCatalog();
      setCatalog(data);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut încărca catalogul de proceduri. Vă rugăm încercați din nou.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Catalog filtered by the already-selected "Tip procedură" (procedure_type_id),
  // NOT by the catalog's own finer `category` grouping.
  const catalogForType = useMemo(
    () => catalog.filter((item) => !procedureTypeId || item.procedure_type_id === procedureTypeId),
    [catalog, procedureTypeId]
  );

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return catalogForType;
    }

    const term = searchTerm.toLowerCase().trim();
    return catalogForType.filter(
      (item) =>
        item.name_ro.toLowerCase().includes(term) ||
        item.name_en.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term) ||
        (item.ipccc_code && item.ipccc_code.toLowerCase().includes(term))
    );
  }, [catalogForType, searchTerm]);

  const groupedItems = useMemo(
    () => groupByCategory(filteredItems, (item) => item.category),
    [filteredItems]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (selectedItem) {
      setSelectedItem(null);
    }
  };

  const handleSelectItem = (item: ProcedureCatalogItem) => {
    setSelectedItem(item);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSelect(item);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSelectedItem(null);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onFreeText('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          handleSelectItem(filteredItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-flat-index="${highlightedIndex}"]`
      ) as HTMLElement | null;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [highlightedIndex]);

  const switchToFreeText = () => {
    setIsFreeText(true);
    setIsOpen(false);
    setSelectedItem(null);
    setSearchTerm('');
  };

  const switchToCatalog = () => {
    setIsFreeText(false);
    onFreeText('');
  };

  if (isFreeText) {
    return (
      <div className={cn('w-full space-y-1', className)}>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={textValue || ''}
            onChange={(e) => onFreeText(e.target.value)}
            placeholder="Ex: Reparare totală tetralogie Fallot"
            disabled={isDisabled}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={switchToCatalog}
            disabled={isDisabled}
            title="Caută în catalogul de proceduri"
          >
            <ListChecks className="mr-2 h-4 w-4" />
            Caută în catalog
          </Button>
        </div>
      </div>
    );
  }

  const displayValue = selectedItem ? selectedItem.name_ro : searchTerm;
  const typeSelected = !!procedureTypeId;

  let flatIndex = -1;

  return (
    <div ref={containerRef} className={cn('relative w-full space-y-1', className)}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={typeSelected ? placeholder : 'Selectați mai întâi tipul procedurii'}
            disabled={isDisabled || isLoading || !typeSelected}
            className={cn('pl-10 pr-10', selectedItem && 'font-medium')}
          />
          {selectedItem && !isDisabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
              onClick={handleClear}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={switchToFreeText}
          disabled={isDisabled}
          title="Introducere manuală a numelui procedurii"
        >
          <PenLine className="mr-2 h-4 w-4" />
          Altele
        </Button>
      </div>

      {isOpen && !isDisabled && typeSelected && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-[320px] overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Se încarcă...</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit rezultate în catalog' : 'Nu sunt proceduri disponibile pentru acest tip'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Folosiți opțiunea &quot;Altele&quot; pentru introducere manuală.
              </p>
            </div>
          ) : (
            groupedItems.map((group) => (
              <div key={group.category}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                  {group.category}
                </div>
                {group.items.map((item) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  return (
                    <div
                      key={item.id}
                      data-flat-index={index}
                      className={cn(
                        'px-3 py-2 cursor-pointer text-sm transition-colors text-foreground',
                        'hover:bg-accent hover:text-accent-foreground',
                        highlightedIndex === index && 'bg-accent text-accent-foreground',
                        selectedItem?.id === item.id && 'bg-primary/10 font-medium'
                      )}
                      onClick={() => handleSelectItem(item)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <div className="font-medium text-foreground">
                        {item.name_ro}
                        {Boolean(item.is_variant) && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">(variantă)</span>
                        )}
                      </div>
                      {item.indication && (
                        <div className="text-foreground/70 truncate">{item.indication}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
